import { createBrowserRouter, Navigate } from "react-router";
import { Suspense, lazy } from "react";
import { motion } from "framer-motion";

import { AuthLayout } from "./layouts/AuthLayout.jsx";
import { DashboardLayout } from "./layouts/DashboardLayout.jsx";
import { ProtectedRoute } from "./routes/ProtectedRoute.jsx";
import { PermissionRoute } from "./routes/PermissionRoute.jsx";
import { PERMISSIONS } from "./constants/permissions";

// Lazy load auth components
const Login = lazy(() => import("./features/auth/Login.jsx"));
const Register = lazy(() => import("./features/auth/Register.jsx"));
const ForgotPassword = lazy(() => import("./features/auth/ForgotPassword.jsx"));
const ResetPassword = lazy(() => import("./features/auth/ResetPassword.jsx"));

// Lazy load CRM components
const ClientsList = lazy(() => import("./features/crm-client/ClientsList.jsx"));
const ClientForm = lazy(() => import("./features/crm-client/ClientForm.jsx"));
const ClientDetails = lazy(() => import("./features/crm-client/ClientDetails.jsx"));
const PriceListsPage = lazy(() => import("./features/crm-client/PriceListsPage.jsx"));
const CampaignsDashboard = lazy(() => import("./features/campaigns/applied-sample-promotion/CampaignsDashboard.jsx"));
const PromotionReport = lazy(() => import("./features/campaigns/promotion-report/PromotionReport.jsx"));
const CampaignMatrixView = lazy(() => import("./features/campaigns/applied-sample-promotion/CampaignMatrixView.jsx"));
const CrmSalesTeamsList = lazy(() => import("./features/crm-sales-teams/CrmSalesTeamsList.jsx"));
const CrmSalesTeamDetailsPage = lazy(() => import("./features/crm-sales-teams/CrmSalesTeamDetailsPage.jsx"));
const CrmDefaultFollowersList = lazy(() => import("./features/crm-default-followers/CrmDefaultFollowersList.jsx"));
const CrmDefaultManagersList = lazy(() => import("./features/crm-default-managers/CrmDefaultManagersList.jsx"));
const CrmMarketCategoriesList = lazy(() => import("./features/crm-market-categories/CrmMarketCategoriesList.jsx"));
const CrmMarketSubCategoriesList = lazy(() => import("./features/crm-market-categories/CrmMarketSubCategoriesList.jsx"));
const CrmCategoriesList = lazy(() => import("./features/crm-categories/CrmCategoriesList.jsx"));
const CrmTagsList = lazy(() => import("./features/crm-tags/CrmTagsList.jsx"));

// Lazy load user & access management components
const EmployeeInvitations = lazy(() => import("./features/invites-and-access/employee-invite/EmployeeInvitations.jsx"));
const EmployeeManagement = lazy(() => import("./features/invites-and-access/employee-management/EmployeeManagement.jsx"));
const History = lazy(() => import("./features/invites-and-access/history/History.jsx"));
const AccessManagement = lazy(() => import("./features/invites-and-access/access-management/AccessManagement.jsx"));
const UserProfilePage = lazy(() => import("./features/profile/UserProfilePage.jsx"));
const CreateRole = lazy(() => import("./features/invites-and-access/access-management/CreateRole.jsx"));
const UpdateRole = lazy(() => import("./features/invites-and-access/access-management/UpdateRole.jsx"));
const BFFProductCodeList = lazy(() => import("./features/master-application-recipe-list/bff-product-code-list/BFFProductCodeList.jsx"));
const AddBFFProductCodePage = lazy(() => import("./features/master-application-recipe-list/bff-product-code-list/AddBFFProductCodePage.jsx"));
const EditBFFProductCodePage = lazy(() => import("./features/master-application-recipe-list/bff-product-code-list/EditBFFProductCodePage.jsx"));
const BFFProductSegmentPage = lazy(() => import("./features/bff-product/segment/BFFProductSegmentPage.jsx"));

// eslint-disable-next-line react-refresh/only-export-components
function ErrorPage() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background text-foreground">
      <div className="text-center">
        <h1 className="mb-4 lg:mb-1.5 xl:mb-2 2xl:mb-3 3xl:mb-4 lg:text-lg xl:text-xl 2xl:text-2xl 3xl:text-4xl font-bold text-red-500">Oops!</h1>
        <p className="mb-4 lg:mb-1.5 xl:mb-2 2xl:mb-3 3xl:mb-4 text-lg lg:text-[10px] xl:text-xs 2xl:text-sm 3xl:text-lg">Something went wrong.</p>
        <p className="text-sm text-gray-500">
          Please try refreshing the page or contact support if the problem
          persists.
        </p>
      </div>
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
function LoadingPage() {
  return (
    <div className="flex items-center justify-center min-h-screen transition-opacity min-w-screen">
      <div className="text-center">
        <motion.img
          src="/logo.png"
          alt="Loading..."
          className=""
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 2, ease: "easeOut", repeat: Infinity, repeatType: "loop" }}
        />
      </div>
    </div>
  );
}

// Wrapper component for lazy loaded components
function LazyWrapper({ children }) {
  return <Suspense fallback={<LoadingPage />}>{children}</Suspense>;
}

const invitesAndAccessRoutes = [
  { path: "employee-management", element: <LazyWrapper><EmployeeManagement /></LazyWrapper> },
  { path: "employee-invitations", element: <LazyWrapper><EmployeeInvitations /></LazyWrapper> },
  { path: "access-management", element: <LazyWrapper><AccessManagement /></LazyWrapper> },
  { path: "access-management/create-role", element: <LazyWrapper><CreateRole /></LazyWrapper> },
  { path: "access-management/update-role/:roleId", element: <LazyWrapper><UpdateRole /></LazyWrapper> },
  { path: "access-history", element: <LazyWrapper><History /></LazyWrapper> },
];

export const router = createBrowserRouter([
  {
    // Root redirect to login
    path: "/",
    element: <Navigate to="/login" replace />,
  },
  {
    // === AUTH LAYOUT ===
    path: "/",
    element: <AuthLayout />,
    errorElement: <ErrorPage />,
    children: [
      { path: "login", element: <LazyWrapper><Login /></LazyWrapper> },
      { path: "register", element: <LazyWrapper><Register /></LazyWrapper> },
      { path: "forgot-password", element: <LazyWrapper><ForgotPassword /></LazyWrapper> },
      { path: "reset-password", element: <LazyWrapper><ResetPassword /></LazyWrapper> },
    ],
  },
  {
    // === DASHBOARD LAYOUT (PROTECTED) ===
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <DashboardLayout />, // UI: sidebar + topbar
        errorElement: <ErrorPage />,
        children: [
          // Redirect dashboard default route to clients
          { path: "dashboard", element: <Navigate to="/clients" replace /> },
          
          // CRM client routes
          {
            path: "clients",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CLIENT.READ}>
                <LazyWrapper><ClientsList /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "clients/create",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CLIENT.CREATE}>
                <LazyWrapper><ClientForm /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "clients/:id",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CLIENT.READ}>
                <LazyWrapper><ClientDetails /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "clients/:id/edit",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CLIENT.UPDATE}>
                <LazyWrapper><ClientForm /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "price-lists",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CLIENT.READ}>
                <LazyWrapper><PriceListsPage /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "campaigns",
            element: <Navigate to="/campaigns/applied-sample-promotion" replace />,
          },
          {
            path: "campaigns/applied-sample-promotion",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CAMPAIGN.READ}>
                <LazyWrapper><CampaignsDashboard /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "campaigns/applied-sample-promotion/:id",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CAMPAIGN.READ}>
                <LazyWrapper><CampaignMatrixView /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "campaigns/bff-product-promotion",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CAMPAIGN.READ}>
                <LazyWrapper><CampaignsDashboard /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "campaigns/bff-product-promotion/:id",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CAMPAIGN.READ}>
                <LazyWrapper><CampaignMatrixView /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "campaigns/promotion-report",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CAMPAIGN.READ}>
                <LazyWrapper><PromotionReport /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "campaigns/promotion-adoption-report",
            element: <Navigate to="/campaigns/promotion-report" replace />,
          },
          {
            path: "settings/sales-teams",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CRM_SALES_TEAM.READ}>
                <LazyWrapper><CrmSalesTeamsList /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "settings/sales-teams/:id",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CRM_SALES_TEAM.READ}>
                <LazyWrapper><CrmSalesTeamDetailsPage /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "settings/default-followers",
            element: (
              <LazyWrapper><CrmDefaultFollowersList /></LazyWrapper>
            ),
          },
          {
            path: "settings/default-managers",
            element: (
              <LazyWrapper><CrmDefaultManagersList /></LazyWrapper>
            ),
          },
          {
            path: "settings/market-categories",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CRM_MARKET_CATEGORY.READ}>
                <LazyWrapper><CrmMarketCategoriesList /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "settings/market-categories/:marketCategoryId/market-sub-categories",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CRM_MARKET_SUB_CATEGORY.READ}>
                <LazyWrapper><CrmMarketSubCategoriesList /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "settings/categories",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CRM_CATEGORY.READ}>
                <LazyWrapper><CrmCategoriesList /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "settings/tags",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.CRM_TAG.READ}>
                <LazyWrapper><CrmTagsList /></LazyWrapper>
              </PermissionRoute>
            ),
          },

          
          // BFF Product
          {
            path: "bff-product-code-list",
            element: <Navigate to="/bff-product/list" replace />,
          },
          {
            path: "bff-product/list",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.BFF_PRODUCT.READ}>
                <LazyWrapper><BFFProductCodeList /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "bff-product/add",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.BFF_PRODUCT.CREATE}>
                <LazyWrapper><AddBFFProductCodePage /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "bff-product/:id",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.BFF_PRODUCT.UPDATE}>
                <LazyWrapper><EditBFFProductCodePage /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "bff-product/segment",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.BFF_PRODUCT_SEGMENT.READ}>
                <LazyWrapper><BFFProductSegmentPage /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "bff-product/segment/:kind",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.BFF_PRODUCT_SEGMENT.READ}>
                <LazyWrapper><BFFProductSegmentPage /></LazyWrapper>
              </PermissionRoute>
            ),
          },
          {
            path: "bff-product/taxonomy/:kind",
            element: (
              <PermissionRoute requiredPermission={PERMISSIONS.BFF_PRODUCT_SEGMENT.READ}>
                <LazyWrapper><BFFProductSegmentPage /></LazyWrapper>
              </PermissionRoute>
            ),
          },

          // Profile & Settings
          { path: "profile", element: <LazyWrapper><UserProfilePage /></LazyWrapper> },
          
          // Admin Invites & Access Management
          ...invitesAndAccessRoutes,
        ],
      },
    ],
  },
]);
